<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          {{ $t('external_coach_billing.title') }}
        </h6>

        <CreateButton
            :label="$t('general.create_invoice')"
            :to="{ name: 'coachBillingCoachingSelection' }">
        </CreateButton>
      </div>

      <div class="element-box">
        <Menu :countUnreadMessagesForAccountant="countUnreadMessagesForAccountant"/>

        <Search
            :filters="filters"
            :loading="isSearching"
            :coachDropdown="coachDropdown"
            :statusDropdown="statusDropdown"
            @search="getCoachBills"/>

        <PerPage
            class="mt-3"
            :values="[10, 25, 50, 100]"
            @update:perPage="getCoachBills"/>

        <ExcelExportButton
            style="margin-right: 15px;"
            :label="$t('general.excel_export')"
            :loading="isExcelLoaded"
            @click="getExcelExport"/>

        <EmptyState
            v-if="!coachBills.data?.length"
            icon="fa-file-invoice"
            title="Keine Rechnungen gefunden"
            subtitle="Es wurden keine Rechnungen gefunden, die Ihren Suchkriterien entsprechen."/>

        <form v-else @submit.prevent="" method="post">
          <div class="table-responsive overflow-auto pt-4">
            <table class="table table-striped">
              <thead>
              <tr>
                <th style="width: 3%;"></th>
                <th>Rechnungs-Nr.</th>
                <th style="width: 20%;">Coach</th>
                <th>Kreditor</th>
                <th>Rechnungsdatum</th>
                <th class="text-end"># Sitzungen</th>
                <th class="text-end">Rechnungsbetrag</th>
                <th class="text-start"><span class="ps-5">Status</span></th>
                <th style="width: 5%;"></th>
                <th style="width: 5%;"></th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(coachBill) in coachBills.data" :key="coachBill.id">
                <td class="text-center">
                  <label class="custom-checkbox position-relative">
                    <input type="checkbox" v-model="selectedBillIds" :value="coachBill.id"
                           class="custom-checkbox position-absolute"/>
                    <div class="custom-check-icon"></div>
                  </label>
                </td>
                <td>GS {{ coachBill?.billNumber }}</td>
                <td>{{ coachBill?.coach?.firstName }} {{ coachBill?.coach?.lastName }}</td>
                <td>{{ coachBill?.coach?.creditor }}</td>
                <td>{{ coachBill.createdAt }}</td>
                <td class="text-end">
                  <span v-if="coachBill.billingType === 'pauschalbetrag'">
                        Pauschalbetrag
                  </span>
                  <span v-else>
                      {{ coachBill.totalUnits }}
                  </span>
                </td>
                <td class="text-end">{{ coachBill.sum }}</td>
                <td class="text-start" style="min-width: 160px">
                  <div class="ps-3">
                    <div v-if="coachBill.status === 'billed' || coachBill.status === 'approved'"
                         class="status-pill green p-2 me-2"
                         :data-title="coachBill.statusTranslation"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else-if="coachBill.status === 'pending'"
                         class="status-pill yellow p-2 me-2"
                         :data-title="coachBill.statusTranslation"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>
                    <div v-else
                         class="status-pill red p-2 me-2"
                         :data-title="`coachBill.statusTranslation`"
                         data-bs-toggle="tooltip"
                         data-original-title=""
                         title="">
                    </div>

                    {{ coachBill.statusTranslation }}
                  </div>
                </td>
                <td style="padding-right: 0; min-width: 130px;">
                  <CancelButton
                      v-if="coachBill.status === 'billed'"
                      :coachBill="coachBill"
                      icon
                      @billCancellation="cancellationBillModal"/>
                </td>
                <td style="padding-right: 0; min-width: 130px;">
                  <PrintButton :coachBill="coachBill" icon/>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="row d-flex align-items-start mt-2">
            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-start mt-4">
              <DownloadButton
                  :label="'Rechnung download'"
                  :loading="isBillDownloaded"
                  @click="downloadBills"/>
            </div>

            <div class="col-lg-6 col-md-6 col-sm-12 d-flex justify-content-end">
              <Pagination
                  v-if="coachBills?.pagination?.total_pages > 1"
                  :totalPages="coachBills?.pagination?.total_pages"
                  :currentPage="coachBills?.pagination?.current_page"
                  :totalItems="coachBills?.pagination?.total_items"
                  :perPage="coachBills?.pagination?.items_per_page"
                  @pagechanged="getCoachBills"/>
            </div>
          </div>

        </form>

      </div>
    </div>

    <ModalConfirm
        v-model="showCancellationBillModal"
        :confirmationQuestion="t('general.approve_question')"
        header=""
        :onConfirm="cancellationBill"/>
  </PageTemplate>
</template>


<script setup>
import coachBillingService from '@/api/CoachBilling';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import {getExternalCoachForBillingDropdown} from '@/shared/dropdowns';
import {downloadFile} from '@/shared/functions';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import CancelButton from '@/views/App/CoachBilling/Accountant/Existing/CancelButton.vue';
import PrintButton from '@/views/App/CoachBilling/Accountant/Existing/PrintButton.vue';
import Search from '@/views/App/CoachBilling/Accountant/Existing/Search.vue';
import Menu from '@/views/App/CoachBilling/Accountant/Menu.vue';
import {computed, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import DownloadButton from "@/components/button/DownloadButton.vue";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();
const filters = ref({
  coachId: '',
  billNumber: '',
  startDate: '',
  endDate: '',
  status: '',
});

const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const coachBills = ref([]);
const coachDropdown = ref([]);
const statusDropdown = ref([]);
const selectedBillIds = ref([]);
const billId = ref('');
const billType = ref('');
const isSearching = ref(false);
const isLoaded = ref(false);
const showCancellationBillModal = ref(false);
const submitForm = ref(false);
const isBillDownloaded = ref(false)
const countUnreadMessagesForAccountant = ref(0);
const isExcelLoaded = ref(false);

watch(submitForm, () => {
  billId.value = '';
  billType.value = '';
  showCancellationBillModal.value = false;
});

async function getCoachBills(page = 1, perPage, passedFilters) {
  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = coachBills.value?.pagination?.items_per_page;

  try {
    const {data} = await coachBillingService.getExistingBillsList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : filters.value),
    });

    coachBills.value = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

async function downloadBills() {
  CloseToast();

  try {
    if (selectedBillIds.value.length === 0) {
      await ErrorToast({
        response: {
          data: {
            error: {
              message: 'Rechnung zum Herunterladen auswählen!'
            }
          }
        }
      });
    } else {
      await downloadFile('/coach-billing/download-bills',
          undefined,
          {billIds: selectedBillIds.value},
          false, 'POST',
          isBillDownloaded
      );
      selectedBillIds.value = [];
    }

  } catch (error) {
    await ErrorToast(error);
  }
}


function cancellationBillModal(passedBillId, passedBillType) {
  billId.value = '';
  billType.value = '';

  showCancellationBillModal.value = true;

  billId.value = passedBillId;
  billType.value = passedBillType;
}

async function cancellationBill() {
  showCancellationBillModal.value = false;

  try {
    if (billType.value === 'private_coaching') {
      await coachBillingService.privateCoachingBillCancellation(billId.value);
    }
    if (billType.value === 'group_coaching_hourly_wages') {
      await coachBillingService.groupCoachingHourlyWagesBillCancellation(billId.value);
    }
    if (billType.value === 'group_coaching_pauschal') {
      await coachBillingService.groupCoachingPauschalBillCancellation(billId.value);
    }
    if (billType.value === 'assisted_living_coaching') {
      await coachBillingService.assistedLivingCoachingBillCancellation(billId.value);
    }
    if (billType.value === 'case_management_coaching') {
      await coachBillingService.caseManagementCoachingBillCancellation(billId.value);
    }

    submitForm.value = false;
    SuccessToast(t('general.toasts.save_success'));
    await getCoachBills();
  } catch (error) {
    await ErrorToast(error);
  }

}

async function countUnreadMessages() {
  try {
    const {data} = await coachBillingService.countUnreadMessagesForAccountant();

    countUnreadMessagesForAccountant.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getStatusDropdown() {
  statusDropdown.value = getEnums?.value?.billStatuses.filter(status => status.value === 'billed' || status.value === 'canceled') || [];
}


async function getExcelExport() {
  await downloadFile('/coach-billing/existing-bills-excel-export',
      undefined,
      filters.value,
      false,
      "GET",
      isExcelLoaded
  )
}

onMounted(async () => {
  CloseToast()
  await Promise.all([
    getCoachBills(),
    getStatusDropdown(),
    getExternalCoachForBillingDropdown(coachDropdown),
    countUnreadMessages(),
  ]);

  isLoaded.value = true;
});

</script>

<style scoped>
.custom-checkbox .custom-check-icon {
  position: relative;
  display: inline-block;
  width: 18px;
  min-width: 18px;
  height: 18px;
  min-height: 18px;
  border: 2px solid var(--primary);
  border-radius: 4px;
  background-color: white;
  margin-right: 8px;
}

.custom-checkbox .custom-check-icon::before {
  content: '';
  position: absolute;
  top: 1px;
  left: 4px;
  width: 6px;
  height: 11px;
  border: solid var(--primary);
  border-width: 0 3px 3px 0;
  transform: rotate(45deg);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.custom-checkbox input[type="checkbox"]:checked + .custom-check-icon::before {
  opacity: 1;
}

.custom-checkbox .custom-check-icon:hover {
  border-color: var(--primary);
}
</style>